/**
 * Centralized PostHog event capture for AI-readable debugging logs.
 *
 * PostHog autocapture records "clicked button" / "clicked span" with empty text
 * because React nests text inside multiple DOM layers. These explicit capture calls
 * produce structured events like "tab_clicked { tab: 'address_contact', page: '/orders' }"
 * that an AI (or human) can read in PostHog's activity feed to understand what a user did.
 *
 * All calls are fire-and-forget — errors are silently swallowed, never break the app.
 */

import posthog from 'posthog-js';

function capture(event: string, properties?: Record<string, unknown>): void {
  try {
    if (!posthog.__loaded) return;
    posthog.capture(event, {
      ...properties,
      $current_url: window.location.href,
    });
  } catch {
    // Silent — analytics must never break the app
  }
}

/** User clicked a tab on a detail page (e.g., "Address & Contact" on an order) */
export function captureTabClicked(tab: string, page?: string): void {
  capture('tab_clicked', { tab, page: page ?? window.location.pathname });
}

/** User clicked a row in a list to open a detail panel or navigate */
export function captureListRowClicked(docname: string): void {
  capture('list_row_clicked', { docname, page: window.location.pathname });
}

/** User saved a document (patch-based save via usePatchSaveDoc) */
export function captureDocSaved(doctype: string, docname: string, fields: string[]): void {
  capture('doc_saved', { doctype, docname, fields_changed: fields });
}

/** User submitted a document (docstatus 0 -> 1) */
export function captureDocSubmitted(doctype: string, docname: string): void {
  capture('doc_submitted', { doctype, docname });
}

/** User cancelled a document (docstatus 1 -> 2) */
export function captureDocCancelled(doctype: string, docname: string): void {
  capture('doc_cancelled', { doctype, docname });
}

/** User amended a document (creates new draft from cancelled) */
export function captureDocAmended(doctype: string, docname: string): void {
  capture('doc_amended', { doctype, docname });
}

// ── Google Ads conversion events (Plan 424) ──

const ACTIVATION_KEY = 'ph_activation_fired';

/** User completed activation (first channel connected OR first conversation) — fires once per user */
export function captureActivationCompleted(trigger: string, tenantId?: string): void {
  try {
    if (localStorage.getItem(ACTIVATION_KEY)) return;
    localStorage.setItem(ACTIVATION_KEY, '1');
  } catch { /* private browsing */ }
  capture('activation_completed', { trigger, tenant_id: tenantId });
}

/** User upgraded to a paid plan */
export function capturePlanUpgraded(planName: string, tenantId?: string): void {
  capture('plan_upgraded', { plan_name: planName, tenant_id: tenantId });
}

/** User connected a marketplace channel (Lazada, Shopee, TikTok, LINE) */
export function captureChannelConnected(channel: string, tenantId?: string): void {
  capture('channel_connected', { channel, tenant_id: tenantId });
}

/** User created a new product/item */
export function captureProductAdded(tenantId?: string): void {
  capture('product_added', { tenant_id: tenantId });
}

/** User invited a team member */
export function captureTeamMemberInvited(tenantId?: string): void {
  capture('team_member_invited', { tenant_id: tenantId });
}

/** User created/published a storefront */
export function captureStorefrontPublished(tenantId?: string): void {
  capture('storefront_published', { tenant_id: tenantId });
}

// ── Onboarding funnel events (Plan 424 — fills gap between signup and activation) ──

/** User saw the intent-specific onboarding page after registration */
export function captureOnboardingViewed(intent: string): void {
  capture('onboarding_viewed', { intent });
}

/** User clicked the primary CTA on the onboarding page (e.g., "เปิด Inbox") */
export function captureOnboardingCompleted(intent: string): void {
  capture('onboarding_completed', { intent });
}

/** User skipped onboarding — clicked "ข้ามขั้นตอนนี้" */
export function captureOnboardingSkipped(intent: string): void {
  capture('onboarding_skipped', { intent });
}

/** User clicked a channel card on the chat onboarding page (LINE OA, Facebook, Instagram) */
export function captureOnboardingChannelClicked(channel: string): void {
  capture('onboarding_channel_clicked', { channel });
}

// ── Print telemetry (src/lib/print/printTelemetry.ts): counts and ids only ──

/** print_attempt / print_blank from printInFrame; print_host when the app page itself is printed. */
export function capturePrintEvent(
  event: 'print_attempt' | 'print_blank' | 'print_host',
  properties: Record<string, unknown>,
): void {
  capture(event, properties);
}
